'use client';
import { useEffect, useRef } from 'react';

const cons = [
  'Multiple separate purchases',
  'Multiple checkout processes',
  'Assets scattered across products',
  'More time spent searching',
  'No central resource library',
];

const pros = [
  'One purchase — $79 total',
  '43 resource collections',
  'Multiple creative categories',
  'One central mega collection',
  'Start using resources faster',
];

export default function ComparisonSection() {
  const ref = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('reveal-hidden');
            entry.target.classList.add('reveal-visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    if (headRef?.current) observer?.observe(headRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="why-bundle"
      className="relative py-20 lg:py-24 bg-section-dark"
      aria-labelledby="comparison-heading"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14 reveal-hidden" ref={headRef}>
          <h2 id="comparison-heading" className="section-heading mb-4" style={{ color: 'var(--foreground)' }}>
            Why Buy the{' '}
            <span className="text-gradient">Complete Bundle?</span>
          </h2>
          <p className="text-lg max-w-xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
            One organized library beats hunting down individual resources every time.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 reveal-hidden" ref={ref}>
          {/* Cons */}
          <div className="compare-col p-8">
            <h3 className="font-bold text-lg mb-6 flex items-center gap-2" style={{ color: 'var(--muted-foreground)' }}>
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ background: 'rgba(239,68,68,0.2)', color: 'rgb(248,113,113)' }}
              >
                ✕
              </span>
              Buying Resources Separately
            </h3>
            <ul className="space-y-4">
              {cons?.map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width={18} height={18} style={{ color: 'rgb(248,113,113)', flexShrink: 0 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Pros */}
          <div className="compare-col highlight p-8">
            <h3 className="font-bold text-lg mb-6 flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ background: 'rgba(124,58,237,0.2)', color: 'rgb(196,181,253)' }}
              >
                ✦
              </span>
              43-Bundle Collection
            </h3>
            <ul className="space-y-4">
              {pros?.map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-sm font-medium" style={{ color: 'var(--foreground)' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width={18} height={18} style={{ color: 'var(--primary)', flexShrink: 0 }}>
                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(124,58,237,0.2)' }}>
              <div className="text-3xl font-extrabold text-gradient">$79</div>
              <div className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>One-time purchase</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
